import { StateCreator } from "zustand"
import toast from "react-hot-toast"
import Swal from "sweetalert2"

import type * as MediaTypes from "@pages/Media/media.types"
import type * as StoreTypes from "@src/types/store"

import { isObject, preConfirmDelete } from "@src/utils"
import { apiService } from "@src/services/apiService"
import { SwalConfig } from "@src/utils/SwalFire"

const initState = {
  listMedias: [],
  listMediasPagination: null,
  queryMedias: {
    type: "All",
    per_page: 20,
    page: 1,
  },
}

export const createMediaSlice: StateCreator<StoreTypes.StoreState, [], [], StoreTypes.MediaSlice> = (
  set,
  get,
) => ({
  ...initState,
  onChangeQueryMedias: (
    value: string | number | Partial<MediaTypes.TMediaQuery>,
    key?: keyof MediaTypes.TMediaQuery,
  ) => {
    if (isObject(value)) {
      set((state) => ({
        queryMedias: { ...state.queryMedias, ...(value as Partial<MediaTypes.TMediaQuery>) },
      }))
    } else if (key && !isObject(value)) {
      set((state) => ({ queryMedias: { ...state.queryMedias, [key]: value } }))
    }
    const { currentProfile, loadMedias } = get()
    if (currentProfile) {
      loadMedias(currentProfile.id)
    }
  },
  loadMedias: async (legacyId: string, params: Record<string, any> = {}) => {
    const { queryMedias, loading } = get()
    if (loading === "FETCHING_MEDIA") return
    try {
      set({ loading: "FETCHING_MEDIA" })
      const response = await apiService.media.getMedias(legacyId, { ...queryMedias, ...params })
      const { status, data, pagination } = response.data as ResponsePagination<IMedia[]>
      if (!status) throw response.data
      set({
        listMedias: data,
        listMediasPagination: pagination,
      })
    } catch (error) {
      toast.error("Error loading medias. Please try again.")
    } finally {
      set({ loading: null })
    }
  },
  addMedia: (medias: IMedia[] | IMedia) => {
    if (Array.isArray(medias)) {
      set((state) => ({
        listMedias: [...state.listMedias, ...medias],
      }))
    } else {
      set((state) => ({
        listMedias: [medias, ...state.listMedias],
      }))
    }
  },
  deleteMedia: async (uuid: string) => {
    try {
      const result = await Swal.fire(
        SwalConfig.confirmDestroy({
          preConfirm: preConfirmDelete.bind(null, {
            uuid,
            promise: apiService.media.deleteFile,
            resource: "media",
          }),
        }),
      )
      if (!result.isConfirmed) return
      set((state) => ({
        listMedias: state.listMedias.filter((media) => media.id !== uuid),
      }))
      const { loadMedias, currentProfile } = get()
      loadMedias(currentProfile?.id || "")
      toast.success("Media deleted successfully")
    } catch (error) {
      toast.error("Error deleting media. Please try again.")
    }
  },
  resetMedia: () => {
    set({ ...initState })
  },
})
